// src/components/artwork/tabs/ArtworkDetailTab.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import EditionSection from "../forms/EditionSection";
import SignatureSection from "../forms/SignatureSection";
import ArtistAutocomplete from "../ArtistAutocomplete";
import ArtistQuoteChip from "../ArtistQuoteChip";
import AiDisclosure from "../../AiDisclosure";

import AdvancedTagsGenerator from "../AdvancedTagsGenerator"; // Version locale
import ApiTagsGenerator from "../ApiTagsGeneratorWithLimits"; // Version avec API et limitations
import { MEDIUM_CATEGORIES, inferMediumCategory } from "../../../utils/mediumCategory";
import { getMediumCategoryLabelI18n, getTypeOfArtLabel } from "../../../utils/artkivLabels";


// -------------------------
// Options canoniques disponibilité
// Stockage DB: snake_case. Affichage: i18n artwork.availability.*
// -------------------------
const AVAILABILITY_OPTIONS = [
  "available",
  "in_restoration",
  "not_found",
  "on_loan",
  "reserved",
  "sold",
  "lost_or_stolen",
  "to_destroy",
];

const LEGACY_CONDITION_MAP = {
  "Excellent": "excellent", "excellent": "excellent",
  "Très bon": "very_good", "Très Bon": "very_good", "très bon": "very_good", "tres_bon": "very_good",
  "Bon": "good", "bon": "good", "Good": "good",
  "Moyen": "fair", "moyen": "fair", "Fair": "fair", "Correct": "fair", "correct": "fair",
  // Grille CUP complète
  "tres_moyen": "very_poor", "Très moyen": "very_poor", "très moyen": "very_poor",
  "tres_abime_restaurable": "heavily_damaged",
  "Très abîmé restaurable": "heavily_damaged", "très abîmé restaurable": "heavily_damaged",
  "À restaurer": "heavily_damaged", "à restaurer": "heavily_damaged", "to_restore": "heavily_damaged",
  "partiellement_restaurable": "partially_restorable",
  "Partiellement restaurable": "partially_restorable", "partiellement restaurable": "partially_restorable",
  "irreparable": "irreparable", "irréparable": "irreparable", "Irréparable": "irreparable",
};

const normalizeConditionKey = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (LEGACY_CONDITION_MAP[raw]) return LEGACY_CONDITION_MAP[raw];

  const normalized = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['']/g, "'")
    .replace(/[\s-]+/g, "_");

  const direct = ["excellent", "very_good", "good", "fair", "very_poor", "heavily_damaged", "partially_restorable", "irreparable"];
  if (direct.includes(normalized)) return normalized;

  if (normalized.includes("irreparable"))                              return "irreparable";
  if (normalized.includes("partiellement"))                           return "partially_restorable";
  if (normalized.includes("tres_abime") || normalized.includes("heavily")) return "heavily_damaged";
  if (normalized.includes("tres_moyen"))                              return "very_poor";
  if (normalized.includes("very") || normalized.includes("tres_bon")) return "very_good";
  if (normalized.includes("excellent"))                               return "excellent";
  if (normalized.includes("bon") || normalized.includes("good"))     return "good";
  if (normalized.includes("moyen") || normalized.includes("fair") || normalized.includes("correct")) return "fair";
  if (normalized.includes("restaur"))                                return "heavily_damaged";

  return "";
};

const getConditionLabel = (t, value) => {
  const key = normalizeConditionKey(value);
  if (!key) return value || t("artworkEdit.detail.notSpecified");
  return t(`artwork.condition.${key}`, key);
};

const LEGACY_AVAILABILITY_MAP = {
  Available: "available",
  Reserved: "reserved",
  "On loan": "on_loan",
  Sold: "sold",
  Disponible: "available",
  "Réservée": "reserved",
  "En prêt": "on_loan",
  Vendue: "sold",
};

const normalizeAvailabilityKey = (value) => {
  if (!value) return "";
  if (LEGACY_AVAILABILITY_MAP[value]) return LEGACY_AVAILABILITY_MAP[value];
  return String(value).trim().toLowerCase().replace(/\s+/g, "_");
};

const getAvailabilityLabel = (t, value) => {
  const key = normalizeAvailabilityKey(value);
  if (!key) return t("artworkEdit.detail.notSpecifiedF");
  return t(`artwork.availability.${key}`, value || key);
};

const MEDIUM_TECHNIQUE_FALLBACKS = {
  oil_on_canvas: { fr: "Huile sur toile", en: "Oil on canvas", de: "Öl auf Leinwand" },
  acrylic_on_canvas: { fr: "Acrylique sur toile", en: "Acrylic on canvas", de: "Acryl auf Leinwand" },
  oil_on_paper: { fr: "Huile sur papier", en: "Oil on paper", de: "Öl auf Papier" },
  acrylic_on_paper: { fr: "Acrylique sur papier", en: "Acrylic on paper", de: "Acryl auf Papier" },
  ink_on_paper: { fr: "Encre sur papier", en: "Ink on paper", de: "Tinte auf Papier" },
  watercolor_on_paper: { fr: "Aquarelle sur papier", en: "Watercolor on paper", de: "Aquarell auf Papier" },
  gouache_on_paper: { fr: "Gouache sur papier", en: "Gouache on paper", de: "Gouache auf Papier" },
  pencil_on_paper: { fr: "Crayon sur papier", en: "Pencil on paper", de: "Bleistift auf Papier" },
  graphite_on_paper: { fr: "Mine de plomb sur papier", en: "Graphite on paper", de: "Graphit auf Papier" },
  charcoal_on_paper: { fr: "Fusain sur papier", en: "Charcoal on paper", de: "Kohle auf Papier" },
  mixed_media: { fr: "Technique mixte", en: "Mixed media", de: "Mischtechnik" },
};

const normalizeMediumTechniqueKey = (value) => {
  if (!value) return "";
  return String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, "_")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

const getLanguagePrefix = (t) => {
  const lang =
    t?.i18n?.resolvedLanguage ||
    t?.i18n?.language ||
    (typeof navigator !== "undefined" ? navigator.language : "fr");
  return String(lang || "fr").slice(0, 2).toLowerCase();
};

const getMediumTechniqueLabel = (t, value) => {
  if (!value) return "";

  const raw = String(value).trim();
  const key = normalizeMediumTechniqueKey(raw);

  if (!raw.includes("_") && !MEDIUM_TECHNIQUE_FALLBACKS[key]) return raw;

  const lang = getLanguagePrefix(t);
  const fallback =
    MEDIUM_TECHNIQUE_FALLBACKS[key]?.[lang] ||
    MEDIUM_TECHNIQUE_FALLBACKS[key]?.fr ||
    raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  if (typeof t !== "function") return fallback;

  return t(`artwork.mediums.${key}`, { defaultValue: fallback });
};

// -------------------------
// Helpers stockage API tags
// -------------------------
const normalizeAcquisitionType = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  const map = {
    purchased: "purchase",
    purchase: "purchase",
    achat: "purchase",
    achetee: "purchase",
    "achetée": "purchase",
    acquired: "purchase",
    acquisition: "purchase",
    artist: "artist",
    artiste: "artist",
    direct_artist: "artist",
    artist_direct: "artist",
    galerie: "gallery",
    gallery: "gallery",
    dealer: "gallery",
    auction: "auction",
    enchere: "auction",
    "enchères": "auction",
    "vente aux encheres": "auction",
    "vente aux enchères": "auction",
    donated: "donation",
    donation: "donation",
    don: "donation",
    gift: "donation",
    inheritance: "inheritance",
    heritage: "inheritance",
    "héritage": "inheritance",
    succession: "inheritance",
    private: "private",
    particulier: "private",
    private_sale: "private",
    other: "other",
    autre: "other",
  };
  return map[raw] || raw;
};

const getAcquisitionTypeLabel = (t, value) => {
  const type = normalizeAcquisitionType(value);
  const keys = {
    purchase: "typePurchase",
    artist: "typeArtist",
    gallery: "typeGallery",
    auction: "typeAuction",
    private: "typePrivate",
    donation: "typeDonation",
    inheritance: "typeInheritance",
    exchange: "typeExchange",
    commission: "typeCommission",
    other: "typeOther",
  };
  return keys[type]
    ? t(`artworkEdit.acquisitionForm.${keys[type]}`)
    : (value || t("artworkEdit.detail.notSpecified"));
};

// -------------------------
// Hook : normalisation année seule → 01/01/XXXX
// Déclenché une seule fois à l'ouverture, uniquement en mode édition
// -------------------------
function useYearNormalization(formData, setFormData, readOnly, setYearModalData) {
  const alertShownRef = React.useRef(false);

  useEffect(() => {
    if (readOnly) return;
    if (alertShownRef.current) return;
    if (!formData?.id) return;

    const rawYear = String(formData?.year || "").trim();
    if (!/^\d{4}$/.test(rawYear)) return;

    alertShownRef.current = true;
    setYearModalData({ year: rawYear });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.id, readOnly]);
}

const normalizeMediumCategoryKey = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  const aliases = {
    print: "prints",
    prints: "prints",
    digital: "nft",
    digital_art: "nft",
    nft: "nft",
    works_on_paper: "work_on_paper",
    work_on_paper: "work_on_paper",
    film: "film_video",
    video: "film_video",
    film_video: "film_video",
  };
  const key = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s-]+/g, "_");
  return aliases[key] || key;
};

const getCanonicalMediumCategory = (data = {}) => {
  const fromDb = data.medium_category || data.medium_family_key || "";
  const normalized = normalizeMediumCategoryKey(fromDb);
  if (normalized) return normalized;
  const inferred = inferMediumCategory(data.medium || data.medium_original || "");
  return inferred && inferred !== "other" ? normalizeMediumCategoryKey(inferred) : "";
};

const getTypeOfArtValue = (data = {}) => data.typeOfArt || "";
const getAvailabilityValue = (data = {}) => data.availability || data.availability_status || data.status_availability || "";
const getConditionValue = (data = {}) => data.condition || data.condition_status || data.conservation_state || data.state_of_conservation || "";

const toNumericOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const hasNumericValue = (value) =>
  value !== null && value !== undefined && value !== "" && !Number.isNaN(Number(value));

const formatCurrencyAmount = (value, currency = "EUR", locale, emptyLabel = "—") => {
  if (!hasNumericValue(value)) return emptyLabel;
  return new Intl.NumberFormat(locale || undefined, {
    style: "currency",
    currency: currency || "EUR",
  }).format(Number(value));
};

const numericOrNull = (value) => {
  if (!hasNumericValue(value)) return null;
  return Number(value);
};

const sameNumericValue = (a, b) => {
  const na = numericOrNull(a);
  const nb = numericOrNull(b);
  return na !== null && nb !== null && na === nb;
};

const getGalleryPricingState = (data = {}) => {
  const isPublicForSale = Boolean(data?.is_public_for_sale);

  // Sécurité anti-fantôme : certaines vues / mappings peuvent injecter
  // valuation_low / valuation_high dans price_low / price_high / sale_price.
  // Si l'œuvre n'est pas publiée galerie et que les valeurs reflètent exactement
  // la valorisation expert, on ne les considère pas comme des prix galerie saisis.
  const lowLooksLikeExpert = sameNumericValue(data?.price_low, data?.valuation_low);
  const highLooksLikeExpert = sameNumericValue(data?.price_high, data?.valuation_high);
  const saleLooksLikeExpertHigh =
    sameNumericValue(data?.sale_price, data?.valuation_high) ||
    sameNumericValue(data?.sale_price, data?.price_high);

  const priceLow = lowLooksLikeExpert && !isPublicForSale ? null : numericOrNull(data?.price_low);
  const priceHigh = highLooksLikeExpert && !isPublicForSale ? null : numericOrNull(data?.price_high);
  const salePrice = saleLooksLikeExpertHigh && !isPublicForSale ? null : numericOrNull(data?.sale_price);

  return {
    isPublicForSale,
    priceLow,
    priceHigh,
    salePrice,
    hasGalleryPrice: priceLow !== null || priceHigh !== null || salePrice !== null || isPublicForSale,
  };
};

// -------------------------
// Composant principal
// -------------------------
const ArtworkDetailTab = ({ formData, setFormData, readOnly = false }) => {
  const { t, i18n } = useTranslation();
  const currentTypeOfArt = getTypeOfArtValue(formData);
  const currentMediumCategory = getCanonicalMediumCategory(formData);
  const currentAvailability = getAvailabilityValue(formData);
  const currentCondition = getConditionValue(formData);
  const galleryPricing = useMemo(() => getGalleryPricingState(formData), [formData]);
  // Contexte enrichi pour l'IA : les bons mots-clés d'une œuvre viennent surtout
  // du titre, de l'artiste, du médium et de l'année — pas seulement du texte
  // libre de la description. On concatène tout ce qui est renseigné.
  const aiTagContext = [
    formData?.title,
    formData?.artist,
    formData?.year,
    getTypeOfArtValue(formData),
    formData?.medium ? getMediumTechniqueLabel(t, formData.medium) : null,
    formData?.description,
  ]
    .filter(Boolean)
    .join(" · ");
  const [tagInput, setTagInput] = useState("");
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [taggingMethod, setTaggingMethod] = useState("manual"); // 'manual', 'local', 'api'

  // Normalisation année seule — modal custom
  const [yearModalData, setYearModalData] = useState(null);
  useYearNormalization(readOnly ? null : formData, setFormData, readOnly, setYearModalData);

  // Fonction générique de MAJ des champs (supporte les chemins avec ".")
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;

    if (!name) return;

    // Gestion des champs imbriqués ex: "acquisitionDetails.type"
    if (name.includes(".")) {
      const [rootKey, ...rest] = name.split(".");

      setFormData((prev) => {
        const prevData = prev || {};
        const rootObj = { ...(prevData[rootKey] || {}) };

        let current = rootObj;
        for (let i = 0; i < rest.length - 1; i += 1) {
          const k = rest[i];
          current[k] = current[k] || {};
          current = current[k];
        }
        current[rest[rest.length - 1]] = finalValue;

        return {
          ...prevData,
          [rootKey]: rootObj,
        };
      });
    } else {
      setFormData((prev) => ({
        ...(prev || {}),
        [name]: finalValue,
      }));
    }
  };

  // === TAGS ===

  // assurer l'existence de formData.tags
  useEffect(() => {
    if (!formData?.tags && setFormData) {
      setFormData((prev) => ({
        ...(prev || {}),
        tags: [],
      }));
    }
  }, [formData, setFormData]);

  const descriptionWords = useMemo(() => {
    const desc = formData?.description || "";
    if (!desc || typeof desc !== "string") return [];
    const words = desc
      .toLowerCase()
      .replace(/[.:!?()"']/g, "")
      .split(/\s+/)
      .filter((word, index, arr) => word.length > 3 && arr.indexOf(word) === index);
    return words || [];
  }, [formData?.description]);

  const suggestions = useMemo(() => {
    if (!tagInput) return [];
    const currentTags = formData?.tags || [];
    return descriptionWords.filter(
      (word) =>
        word.startsWith(tagInput.toLowerCase()) &&
        !currentTags.includes(word)
    );
  }, [tagInput, descriptionWords, formData?.tags]);

  // activer les suggestions quand la description est assez longue
  useEffect(() => {
    if (formData?.description && formData.description.length > 50) {
      setShowTagSuggestions(true);
    } else {
      setShowTagSuggestions(false);
    }
  }, [formData?.description]);

  const updateTags = (newTags) => {
    setFormData((prev) => ({
      ...(prev || {}),
      tags: newTags || [],
    }));
  };

  const addTag = (tag) => {
    if (!tag) return;
    const normalizedTag = tag.trim().toLowerCase();
    if (!normalizedTag) return;

    const currentTags = formData?.tags || [];
    if (currentTags.includes(normalizedTag)) return;
    if (currentTags.length >= 10) {
      alert(t("artworkEdit.detail.tagsLimit"));
      return;
    }

    updateTags([...currentTags, normalizedTag]);
    setTagInput("");
  };

  const removeTag = (tagToRemove) => {
    const currentTags = formData?.tags || [];
    updateTags(currentTags.filter((t) => t !== tagToRemove));
  };

  const clearTags = () => {
    updateTags([]);
  };

  const acquisitionDetails = formData?.acquisitionDetails || {};

  // Devise vente / galerie : uniquement pour sale_price, price_low, price_high.
  const galleryCurrency = formData?.currency || "EUR";

  // Devise d’acquisition : distincte de currency.
  const acquisitionCurrency =
    formData?.acquisition_currency ||
    acquisitionDetails.acquisition_currency ||
    acquisitionDetails.currency ||
    "EUR";

  // Devise de valorisation interne. Aujourd’hui ARTKIV consolide les estimations en EUR.
  const valuationCurrency = "EUR";

  const currentEstimatedValue =
    formData?.current_estimated_value ??
    formData?.valuation_estimated_value ??
    formData?.estimated_value ??
    formData?.value ??
    null;

  const acquisitionType = normalizeAcquisitionType(
    formData?.acquisition_type ||
    acquisitionDetails.type ||
    acquisitionDetails.acquisition_type ||
    ""
  );
  const acquisitionDate =
    formData?.acquisition_date ||
    acquisitionDetails.date ||
    acquisitionDetails.acquisition_date ||
    "";
  const acquisitionSource =
    formData?.acquisition_source ||
    acquisitionDetails.source ||
    acquisitionDetails.acquisition_source ||
    "";
  const acquisitionPrice =
    formData?.acquisition_price ??
    acquisitionDetails.acquisition_price ??
    acquisitionDetails.price ??
    acquisitionDetails.purchase_price ??
    acquisitionDetails.lot_total ??
    null;
  const acquisitionPriceEur = formData?.acquisition_price_eur ?? null;
  const acquisitionExchangeRate = formData?.acquisition_exchange_rate ?? null;
  const auctionHouse =
    formData?.auction_house || acquisitionDetails.auction_house || "";
  const auctionSaleName =
    formData?.auction_sale_name || acquisitionDetails.auction_sale_name || "";
  const auctionSaleDate =
    formData?.auction_sale_date || acquisitionDetails.auction_sale_date || "";
  const auctionLotNumber =
    formData?.auction_lot_number ||
    acquisitionDetails.auction_lot_number ||
    acquisitionDetails.lot_number ||
    "";
  const auctionHammerPrice =
    formData?.auction_hammer_price ??
    acquisitionDetails.auction_hammer_price ??
    acquisitionDetails.hammer_price ??
    null;
  const auctionFees =
    formData?.auction_fees ?? acquisitionDetails.auction_fees ?? null;
  const hasAuctionDetails = acquisitionType === "auction" && Boolean(
    auctionHouse || auctionLotNumber || auctionSaleName || auctionSaleDate ||
    auctionHammerPrice != null || auctionFees != null
  );

  // ======================
  // MODE LECTURE SEULE
  // ======================
  if (readOnly) {
    return (
      <div className="space-y-8">
        {/* Informations principales */}
        <section>
          <h2 className="text-xl font-serif font-semibold mb-4">
            {t("artworkEdit.detail.generalInformation")}
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.artist")}</h3>
              <p className="font-serif">
                {formData?.artist || t("artworkEdit.detail.notSpecified")}
              </p>
            </div>
            <div>
              <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.year")}</h3>
              <p className="font-serif">
                {formData?.year || t("artworkEdit.detail.notSpecifiedF")}
              </p>
            </div>
            <div>
              <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.typeOfArt")}</h3>
              <p className="font-serif">
                {currentTypeOfArt ? getTypeOfArtLabel(currentTypeOfArt, t) : t("artworkEdit.detail.notSpecified")}
              </p>
            </div>
            <div>
              <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.medium")}</h3>
              <p className="font-serif">
                {currentMediumCategory
                  ? <span style={{ color: "#c9a961", fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", marginRight: 6 }}>
                      {getMediumCategoryLabelI18n(currentMediumCategory, t)}
                    </span>
                  : null}
                {formData?.medium
                  ? getMediumTechniqueLabel(t, formData.medium)
                  : (!currentMediumCategory ? t("artworkEdit.detail.notSpecified") : "")}
              </p>
            </div>
            <div>
              <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.availability")}</h3>
              <p className="font-serif">
                {getAvailabilityLabel(t, currentAvailability)}
              </p>
            </div>
            <div>
              <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.condition")}</h3>
              <p className="font-serif">
                {getConditionLabel(t, currentCondition)}
              </p>
            </div>
          </div>
        </section>

        {/* Signature */}
        <section>
          <h2 className="text-xl font-serif font-semibold mb-4">{t("artworkEdit.detail.signatureAuthentication")}</h2>
          <SignatureSection
            formData={formData}
            handleInputChange={handleInputChange}
            readOnly
          />
        </section>

        {/* Édition / multiples */}
        <section>
          <h2 className="text-xl font-serif font-semibold mb-4">
            {t("artworkEdit.detail.editionInformation")}
          </h2>
          <EditionSection
            formData={formData}
            handleInputChange={handleInputChange}
            readOnly
          />
        </section>

        {/* Détails d'acquisition */}
        <section>
          <h2 className="text-xl font-serif font-semibold mb-4">
            {t("artworkEdit.detail.acquisition")}
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.acquisitionType")}</h3>
              <p className="font-serif">
                {getAcquisitionTypeLabel(t, acquisitionType)}
              </p>
            </div>
            <div>
              <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.acquisitionDate")}</h3>
              <p className="font-serif">
                {acquisitionDate
                  ? new Date(acquisitionDate).toLocaleDateString(i18n.language)
                  : t("artworkEdit.detail.notSpecifiedF")}
              </p>
            </div>
            <div>
              <h3 className="text-gray-500 text-sm">{t("artworkEdit.acquisitionForm.sourceGeneric")}</h3>
              <p className="font-serif">
                {acquisitionSource || t("artworkEdit.detail.notSpecifiedF")}
              </p>
            </div>
            <div>
              <h3 className="text-gray-500 text-sm">
                {t("artworkEdit.detail.acquisitionPrice")}
              </h3>
              <p className="font-serif">
                {formatCurrencyAmount(acquisitionPrice, acquisitionCurrency, i18n.language, t("artworkEdit.detail.notSpecified"))}
              </p>
              {acquisitionCurrency !== "EUR" && hasNumericValue(acquisitionPriceEur) && (
                <p className="text-xs text-gray-500 mt-1">
                  ≈ {formatCurrencyAmount(acquisitionPriceEur, "EUR", i18n.language)}
                  {hasNumericValue(acquisitionExchangeRate)
                    ? ` · ${t("artworkEdit.detail.exchangeRateShort", { rate: Number(acquisitionExchangeRate).toLocaleString(i18n.language, { maximumFractionDigits: 6 }) })}`
                    : ""}
                </p>
              )}
            </div>
          </div>

          {hasAuctionDetails && (
            <div className="mt-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-600 mb-3">{t("artworkEdit.detail.auctionDetails")}</h3>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <h4 className="text-gray-500 text-sm">{t("artworkEdit.detail.auctionHouse")}</h4>
                  <p className="font-serif">{auctionHouse || t("artworkEdit.detail.notSpecifiedF")}</p>
                </div>
                <div>
                  <h4 className="text-gray-500 text-sm">{t("artworkEdit.detail.lot")}</h4>
                  <p className="font-serif">{auctionLotNumber || t("artworkEdit.detail.notSpecified")}</p>
                </div>
                <div>
                  <h4 className="text-gray-500 text-sm">{t("artworkEdit.detail.sale")}</h4>
                  <p className="font-serif">{auctionSaleName || t("artworkEdit.detail.notSpecifiedF")}</p>
                </div>
                <div>
                  <h4 className="text-gray-500 text-sm">{t("artworkEdit.detail.saleDate")}</h4>
                  <p className="font-serif">
                    {auctionSaleDate ? new Date(auctionSaleDate).toLocaleDateString(i18n.language) : t("artworkEdit.detail.notSpecifiedF")}
                  </p>
                </div>
                <div>
                  <h4 className="text-gray-500 text-sm">{t("artworkEdit.detail.hammerPrice")}</h4>
                  <p className="font-serif">
                    {formatCurrencyAmount(auctionHammerPrice, acquisitionCurrency, i18n.language, t("artworkEdit.detail.notSpecified"))}
                  </p>
                </div>
                <div>
                  <h4 className="text-gray-500 text-sm">{t("artworkEdit.detail.fees")}</h4>
                  <p className="font-serif">
                    {formatCurrencyAmount(auctionFees, acquisitionCurrency, i18n.language, t("artworkEdit.detail.notSpecifiedPlural"))}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Valeur actuelle estimée */}
          <div className="mt-4">
            <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.estimatedValue")}</h3>
            <p className="font-serif">
              {formatCurrencyAmount(currentEstimatedValue, valuationCurrency, i18n.language, t("artworkEdit.detail.notSpecified"))}
            </p>
          </div>
        </section>

        {/* Prix / galerie : uniquement les champs galerie réellement saisis.
            Ne jamais recycler valuation_low / valuation_high ici. */}
        {galleryPricing.hasGalleryPrice && (
          <section>
            <h2 className="text-xl font-serif font-semibold mb-4">{t("artworkEdit.detail.priceGallery")}</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.priceLow")}</h3>
                <p className="font-serif">{formatCurrencyAmount(galleryPricing.priceLow, galleryCurrency, i18n.language, t("artworkEdit.detail.notSpecified"))}</p>
              </div>
              <div>
                <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.priceHigh")}</h3>
                <p className="font-serif">{formatCurrencyAmount(galleryPricing.priceHigh, galleryCurrency, i18n.language, t("artworkEdit.detail.notSpecified"))}</p>
              </div>
              <div>
                <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.salePriceGallery")}</h3>
                <p className="font-serif">{formatCurrencyAmount(galleryPricing.salePrice, galleryCurrency, i18n.language, t("artworkEdit.detail.notSpecified"))}</p>
              </div>
              <div>
                <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.galleryPublication")}</h3>
                <p className="font-serif">
                  {galleryPricing.isPublicForSale
                    ? t("common.yes")
                    : t("common.no")}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Valorisation expert : distincte de la fourchette galerie */}
        {(hasNumericValue(formData?.valuation_low) || hasNumericValue(formData?.valuation_high)) && (
          <section>
            <h2 className="text-xl font-serif font-semibold mb-4">{t("artworkEdit.detail.expertValuation")}</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.priceLow")}</h3>
                <p className="font-serif">{formatCurrencyAmount(formData?.valuation_low, valuationCurrency, i18n.language, t("artworkEdit.detail.notSpecified"))}</p>
              </div>
              <div>
                <h3 className="text-gray-500 text-sm">{t("artworkEdit.detail.priceHigh")}</h3>
                <p className="font-serif">{formatCurrencyAmount(formData?.valuation_high, valuationCurrency, i18n.language, t("artworkEdit.detail.notSpecified"))}</p>
              </div>
            </div>
          </section>
        )}

        {/* Description */}
        <section>
          <h2 className="text-xl font-serif font-semibold mb-2">
            {t("artworkEdit.detail.description")}
          </h2>
          <p className="font-serif text-gray-800 whitespace-pre-line">
            {formData?.description || t("artworkEdit.detail.noDescription")}
          </p>
        </section>

        {/* Tags */}
        <section>
          <h2 className="text-xl font-serif font-semibold mb-2">
            {t("artworkEdit.detail.tagsTitle")}
          </h2>
          {formData?.tags && formData.tags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-gray-200 px-2 py-1 rounded-full text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">{t("artworkEdit.detail.noKeywords")}</p>
          )}
        </section>
      </div>
    );
  }

  // ======================
  // MODE ÉDITION
  // ======================
  return (
    <div className="space-y-6">
      {/* Titre + N° Inventaire */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <label
            htmlFor="title"
            className="block text-sm font-medium text-gray-700"
          >
            {t("artworkEdit.detail.titleLabel")}
          </label>
          <input
            type="text"
            name="title"
            id="title"
            value={formData?.title || ""}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
            placeholder={t("artworkEdit.detail.titleLabel")}
          />
        </div>

        <div>
          <label
            htmlFor="inventory_number"
            className="block text-sm font-medium text-gray-700"
          >
            {t("artworkEdit.detail.inventoryNumber")}
          </label>
          <input
            type="text"
            name="inventory_number"
            id="inventory_number"
            value={formData?.inventory_number || ""}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
            placeholder="Ex : XF-166-ACHA"
          />
        </div>
      </div>

      {/* Ligne Artiste / Année */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="artist"
            className="block text-sm font-medium text-gray-700"
          >
            {t("artworkEdit.detail.artist")}
          </label>
          <ArtistAutocomplete
            name="artist"
            id="artist"
            value={formData?.artist || ""}
            onChange={(e) => {
              handleInputChange(e);
              // Saisie manuelle → on délie l'artiste canonique (re-sélection nécessaire).
              setFormData((prev) => ({ ...(prev || {}), artist_id: null }));
            }}
            onSelectArtist={(cand) => {
              // Sélection dans le référentiel → lien persistant vers l'artiste canonique.
              setFormData((prev) => ({ ...(prev || {}), artist: cand.name, artist_id: cand.id || null }));
            }}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          />
          {formData?.artist_id && <ArtistQuoteChip artistId={formData.artist_id} />}
        </div>

        <div>
          <label
            htmlFor="year"
            className="block text-sm font-medium text-gray-700"
          >
            {t("artworkEdit.detail.year")}
          </label>
          <input
            type="text"
            name="year"
            id="year"
            value={formData?.year || ""}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          />
          {formData?._yearNormalizedToJan1 && (
            <p className="mt-1 text-xs italic" style={{ color: "#c9a961" }}>
              {t("artworkEdit.detail.yearHint")}
            </p>
          )}
        </div>
      </div>

      {/* Type d'art / Catégorie médium / Médium détail */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label
            htmlFor="typeOfArt"
            className="block text-sm font-medium text-gray-700"
          >
            {t("artworkEdit.detail.typeOfArt")}
          </label>
          <select
            name="typeOfArt"
            id="typeOfArt"
            value={currentTypeOfArt}
            onChange={(e) => {
              const value = e.target.value;
              setFormData((prev) => ({
                ...(prev || {}),
                typeOfArt: value,
              }));
            }}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          >
            <option value="">{t("artworkEdit.detail.select")}</option>
            {["contemporary_art","modern_art","impressionism","nineteenth_century_art","old_masters","antiquities","asian_art","middle_eastern_art","african_art","latin_american_art","tribal_art","street_art","photography","prints_multiples","digital_art","design","decorative_arts","other"].map(key => (
              <option key={key} value={key}>{t(`artwork.typeOfArt.${key}`, key)}</option>
            ))}
          </select>
        </div>

        {/* Catégorie médium (alimente medium_category) */}
        <div>
          <label
            htmlFor="medium_category"
            className="block text-sm font-medium text-gray-700"
          >
            {t("artworkEdit.detail.mediumCategory")}
          </label>
          <select
            name="medium_category"
            id="medium_category"
            value={currentMediumCategory}
            onChange={(e) => {
              const value = normalizeMediumCategoryKey(e.target.value);
              setFormData((prev) => ({
                ...(prev || {}),
                medium_category: value,
                medium_family_key: value,
              }));
            }}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          >
            <option value="">{t("artworkEdit.detail.select")}</option>
            {MEDIUM_CATEGORIES.map((cat) => {
              const value = normalizeMediumCategoryKey(cat.value);
              return (
                <option key={cat.value} value={value}>
                  {getMediumCategoryLabelI18n(value, t)}
                </option>
              );
            })}
          </select>
        </div>

        {/* Médium détail — texte libre (alimente medium) */}
        <div>
          <label
            htmlFor="medium"
            className="block text-sm font-medium text-gray-700"
          >
            {t("artworkEdit.detail.medium")}
          </label>
          <input
            type="text"
            name="medium"
            id="medium"
            value={formData?.medium ? getMediumTechniqueLabel(t, formData.medium) : ""}
            onChange={(e) => {
              const newTechnique = e.target.value;
              const suggested = newTechnique.length > 3
                ? inferMediumCategory(newTechnique)
                : null;
              setFormData((prev) => ({
                ...prev,
                medium: newTechnique,
                ...(suggested && suggested !== "other"
                  ? {
                      medium_category: normalizeMediumCategoryKey(suggested),
                      medium_family_key: normalizeMediumCategoryKey(suggested),
                    }
                  : {}),
              }));
            }}
            placeholder={t("artworkEdit.detail.mediumPlaceholder")}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          />
          {/* Affiche la catégorie auto-détectée */}
          {formData?.medium && currentMediumCategory && (
            <p className="mt-1 text-xs" style={{ color: "#c9a961" }}>
              ↳ {getMediumCategoryLabelI18n(currentMediumCategory, t)}
            </p>
          )}
        </div>
      </div>

      {/* Sujet / disponibilité / état */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label
            htmlFor="subjectMatter"
            className="block text-sm font-medium text-gray-700"
          >
            {t("artworkEdit.detail.subject")}
          </label>
          <input
            type="text"
            id="subjectMatter"
            name="subjectMatter"
            value={formData?.subjectMatter || ""}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
            placeholder={t("artworkEdit.detail.subjectPlaceholder")}
          />
        </div>

        <div>
          <label
            htmlFor="availability"
            className="block text-sm font-medium text-gray-700"
          >
            {t("artworkEdit.detail.availability")}
          </label>
          <select
            id="availability"
            name="availability"
            value={normalizeAvailabilityKey(currentAvailability)}
            onChange={(e) => {
              const value = normalizeAvailabilityKey(e.target.value);
              setFormData((prev) => ({ ...(prev || {}), availability: value }));
            }}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          >
            <option value="">{t("artworkEdit.detail.select")}</option>
            {AVAILABILITY_OPTIONS.map((key) => (
              <option key={key} value={key}>
                {t(`artwork.availability.${key}`, key)}
              </option>
            ))}
          </select>
        </div>

        {/* ── Convention de prêt sortant ── */}
        {normalizeAvailabilityKey(currentAvailability) === "on_loan" && (
          <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, padding: "14px 16px" }}>
            <div style={{ fontSize: 12, color: "#0369a1", fontFamily: "system-ui", fontWeight: 700, marginBottom: 12 }}>
              ↗ {t("artworkEdit.detail.loan.outgoing")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 11, color: "#0369a1", fontFamily: "system-ui", marginBottom: 4 }}>{t("artworkEdit.detail.loan.institution")}</label>
                <input type="text"
                  value={formData?.loan_institution || ""}
                  onChange={(e) => setFormData((prev) => ({ ...(prev || {}), loan_institution: e.target.value }))}
                  placeholder={t("artworkEdit.detail.loan.institutionPlaceholder")}
                  className="block w-full border border-blue-200 rounded py-2 px-3"
                  style={{ fontSize: 13, fontFamily: "system-ui", background: "#fff" }}
                  disabled={readOnly}
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 11, color: "#0369a1", fontFamily: "system-ui", marginBottom: 4 }}>{t("artworkEdit.detail.loan.contact")}</label>
                <input type="text"
                  value={formData?.loan_contact || ""}
                  onChange={(e) => setFormData((prev) => ({ ...(prev || {}), loan_contact: e.target.value }))}
                  placeholder={t("artworkEdit.detail.loan.contactPlaceholder")}
                  className="block w-full border border-blue-200 rounded py-2 px-3"
                  style={{ fontSize: 13, fontFamily: "system-ui", background: "#fff" }}
                  disabled={readOnly}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, color: "#0369a1", fontFamily: "system-ui", marginBottom: 4 }}>{t("artworkEdit.detail.loan.startDate")}</label>
                <input type="date"
                  value={formData?.loan_start_date || ""}
                  onChange={(e) => setFormData((prev) => ({ ...(prev || {}), loan_start_date: e.target.value }))}
                  className="block w-full border border-blue-200 rounded py-2 px-3"
                  style={{ fontSize: 13, fontFamily: "system-ui", background: "#fff" }}
                  disabled={readOnly}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, color: "#0369a1", fontFamily: "system-ui", marginBottom: 4 }}>{t("artworkEdit.detail.loan.endDate")}</label>
                <input type="date"
                  value={formData?.loan_end_date || ""}
                  onChange={(e) => setFormData((prev) => ({ ...(prev || {}), loan_end_date: e.target.value }))}
                  className="block w-full border border-blue-200 rounded py-2 px-3"
                  style={{ fontSize: 13, fontFamily: "system-ui", background: "#fff" }}
                  disabled={readOnly}
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 11, color: "#0369a1", fontFamily: "system-ui", marginBottom: 4 }}>{t("artworkEdit.detail.loan.contractRef")}</label>
                <input type="text"
                  value={formData?.loan_contract_ref || ""}
                  onChange={(e) => setFormData((prev) => ({ ...(prev || {}), loan_contract_ref: e.target.value }))}
                  placeholder={t("artworkEdit.detail.loan.contractRefPlaceholder")}
                  className="block w-full border border-blue-200 rounded py-2 px-3"
                  style={{ fontSize: 13, fontFamily: "system-ui", background: "#fff" }}
                  disabled={readOnly}
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 11, color: "#0369a1", fontFamily: "system-ui", marginBottom: 4 }}>{t("artworkEdit.detail.loan.conditions")}</label>
                <textarea
                  value={formData?.loan_conditions || ""}
                  onChange={(e) => setFormData((prev) => ({ ...(prev || {}), loan_conditions: e.target.value }))}
                  placeholder={t("artworkEdit.detail.loan.conditionsPlaceholder")}
                  rows={2}
                  className="block w-full border border-blue-200 rounded py-2 px-3"
                  style={{ fontSize: 13, fontFamily: "system-ui", background: "#fff", resize: "vertical" }}
                  disabled={readOnly}
                />
              </div>
            </div>
            <p style={{ fontSize: 11, color: "#0369a1", marginTop: 10, fontFamily: "system-ui" }}>
              {t("artworkEdit.detail.loan.followUpPrefix")} {" "}
              <a href="/deposits" style={{ fontWeight: 700, textDecoration: "underline" }}>{t("artworkEdit.detail.loan.depositsLoans")}</a>.
            </p>
          </div>
        )}

        {/* ── Champ conditionnel : Perdue / Volée ── */}
        {(normalizeAvailabilityKey(currentAvailability) === "lost_or_stolen") && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca",
            borderRadius: 6, padding: "12px 14px",
          }}>
            <label style={{ display: "block", fontSize: 12, color: "#dc2626", fontFamily: "system-ui", marginBottom: 6, fontWeight: 600 }}>
              ⚠ {t("artworkEdit.detail.loss.circumstances")}
            </label>
            <input
              type="text"
              value={formData?.storage_location_detail || ""}
              onChange={(e) => setFormData((prev) => ({ ...(prev || {}), storage_location_detail: e.target.value }))}
              placeholder={t("artworkEdit.detail.loss.circumstancesPlaceholder")}
              className="mt-1 block w-full border border-red-200 rounded-md shadow-sm py-2 px-3"
              style={{ fontSize: 13, fontFamily: "system-ui", background: "#fff" }}
              disabled={readOnly}
            />
            <p style={{ fontSize: 11, color: "#dc2626", marginTop: 6, fontFamily: "system-ui" }}>
              {t("artworkEdit.detail.loss.followUpPrefix")} {" "}
              <a href="/pertes-vols" style={{ fontWeight: 700, textDecoration: "underline" }}>{t("artworkEdit.detail.loss.lossesThefts")}</a>{" "}
              {t("artworkEdit.detail.loss.followUpSuffix")}
            </p>
          </div>
        )}

        <div>
          <label
            htmlFor="condition"
            className="block text-sm font-medium text-gray-700"
          >
            {t("artworkEdit.detail.condition")}
          </label>
          <select
            id="condition"
            name="condition"
            value={normalizeConditionKey(currentCondition) || ""}
            onChange={(e) => {
              const value = normalizeConditionKey(e.target.value);
              setFormData((prev) => ({ ...(prev || {}), condition: value }));
            }}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          >
            <option value="">{t("artworkEdit.detail.select")}</option>
            {["excellent","very_good","good","fair","very_poor","heavily_damaged","partially_restorable","irreparable"].map(key => (
              <option key={key} value={key}>{t(`artwork.condition.${key}`, key)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Notes sur l'état */}
      <div>
        <label
          htmlFor="conditionNotes"
          className="block text-sm font-medium text-gray-700"
        >
          {t("artworkEdit.detail.conditionNotes")}
        </label>
        <textarea
          id="conditionNotes"
          name="conditionNotes"
          value={formData?.conditionNotes || ""}
          onChange={handleInputChange}
          rows={3}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          placeholder={t("artworkEdit.detail.conditionNotesPlaceholder")}
        />
      </div>

      {/* Signature */}
      <div className="border p-4 rounded-md space-y-4">
        <h3 className="font-medium">{t("artworkEdit.detail.signature")}</h3>
        <SignatureSection
          formData={formData}
          handleInputChange={handleInputChange}
          readOnly={false}
        />
      </div>

      {/* Édition / multiples */}
      <div className="border p-4 rounded-md space-y-4">
        <h3 className="font-medium">{t("artworkEdit.detail.edition")}</h3>
        <EditionSection
          formData={formData}
          handleInputChange={handleInputChange}
          readOnly={false}
        />
      </div>


        {/* Valeur actuelle */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <label
              htmlFor="value"
              className="block text-sm font-medium text-gray-700"
            >
              {t("artworkEdit.detail.valueLabel")}
            </label>
            <input
              type="number"
              id="value"
              name="value"
              value={formData?.current_estimated_value ?? formData?.value ?? ""}
              onChange={(e) => {
                const numericValue = toNumericOrNull(e.target.value);
                setFormData((prev) => ({
                  ...(prev || {}),
                  value: numericValue,
                  current_estimated_value: numericValue,
                  estimated_value: numericValue,
                }));
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
              min="0"
            />
          </div>
          <div>
            <label
              htmlFor="currency"
              className="block text-sm font-medium text-gray-700"
            >
              {t("artworkEdit.detail.currency")}
            </label>
            <select
              id="currency"
              name="currency"
              value={galleryCurrency}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
            >
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CHF">CHF</option>
              <option value="HKD">HKD</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {t("artworkEdit.detail.currencyGalleryHelp")}
            </p>
          </div>
        </div>

      {/* Prix / galerie */}
      <div className="border border-gray-200 rounded-lg p-5 mt-2 bg-white space-y-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">{t("artworkEdit.detail.priceGallery")}</p>
          <p className="text-xs text-gray-500">
            {t("artworkEdit.detail.priceGalleryHelp")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="price_low" className="block text-sm font-medium text-gray-700">
              {t("artworkEdit.detail.priceLow")}
            </label>
            <input
              type="number"
              id="price_low"
              name="price_low"
              value={formData?.price_low ?? ""}
              onChange={(e) => {
                const numericValue = toNumericOrNull(e.target.value);
                setFormData((prev) => ({ ...(prev || {}), price_low: numericValue }));
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
              min="0"
              step="1"
              placeholder="€"
            />
          </div>

          <div>
            <label htmlFor="price_high" className="block text-sm font-medium text-gray-700">
              {t("artworkEdit.detail.priceHigh")}
            </label>
            <input
              type="number"
              id="price_high"
              name="price_high"
              value={formData?.price_high ?? ""}
              onChange={(e) => {
                const numericValue = toNumericOrNull(e.target.value);
                setFormData((prev) => ({ ...(prev || {}), price_high: numericValue }));
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
              min="0"
              step="1"
              placeholder="€"
            />
          </div>

          <div>
            <label htmlFor="sale_price" className="block text-sm font-medium text-gray-700">
              {t("artworkEdit.detail.salePriceGallery")}
            </label>
            <input
              type="number"
              id="sale_price"
              name="sale_price"
              value={formData?.sale_price ?? ""}
              onChange={(e) => {
                const numericValue = toNumericOrNull(e.target.value);
                setFormData((prev) => ({ ...(prev || {}), sale_price: numericValue }));
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
              min="0"
              step="1"
              placeholder="€"
            />
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            name="is_public_for_sale"
            checked={Boolean(formData?.is_public_for_sale)}
            onChange={(e) => {
              setFormData((prev) => ({ ...(prev || {}), is_public_for_sale: e.target.checked }));
            }}
            className="rounded border-gray-300"
          />
          {t("artworkEdit.detail.publicForSale")}
        </label>
      </div>

      {/* ── Valorisation expert ─────────────────────────────────────────── */}
      {(formData?.valuation_low || formData?.valuation_high || formData?.cup_comparables?.gallery_price_cap) && (
        <div className="border border-gray-200 rounded-lg p-5 mt-2 bg-gray-50">
          <p className="text-xs uppercase tracking-widest text-gray-400 mb-3">{t("artworkEdit.detail.expertValuation")}</p>

          {/* Fourchette et méthode */}
          <div className="flex flex-wrap gap-6 mb-4">
            {(formData.valuation_low || formData.valuation_high) && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">{t("artworkEdit.detail.valuationRange")}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formData.valuation_low ? `${formatCurrencyAmount(formData.valuation_low, valuationCurrency, i18n.language)}` : '—'}
                  {' – '}
                  {formData.valuation_high ? `${formatCurrencyAmount(formData.valuation_high, valuationCurrency, i18n.language)}` : '—'}
                </p>
              </div>
            )}
            {formData?.cup_comparables?.gallery_price_cap && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">{t("artworkEdit.detail.galleryCap")}</p>
                <p className="text-base font-medium text-gray-700">
                  {formatCurrencyAmount(formData.cup_comparables.gallery_price_cap, valuationCurrency, i18n.language)}
                  <span className="ml-1 text-[11px] text-gray-400 font-normal">{t("artworkEdit.detail.indicative")}</span>
                </p>
              </div>
            )}
            {formData.expert_date && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">{t("artworkEdit.detail.expert.date")}</p>
                <p className="text-sm text-gray-700">{String(formData.expert_date).slice(0, 10)}</p>
              </div>
            )}
            {formData.valuation_method && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">{t("artworkEdit.detail.expert.method")}</p>
                <p className="text-sm text-gray-700">{formData.valuation_method}</p>
              </div>
            )}
          </div>

          {/* Note expert */}
          {formData.expert_note && (
            <div className="mb-4 border-l-2 border-gray-300 pl-3">
              <p className="text-xs text-gray-500 mb-1">{t("artworkEdit.detail.expert.note")}</p>
              <p className="text-sm text-gray-700 leading-relaxed">{formData.expert_note}</p>
            </div>
          )}

          {/* Comparables utilisés */}
          {Array.isArray(formData?.cup_comparables?.comparables_used) && formData.cup_comparables.comparables_used.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">
                {t("artworkEdit.detail.expert.comparablesUsed", { count: formData.cup_comparables.comparables_used.length })}
              </p>
              <div className="divide-y divide-gray-100 border border-gray-200 rounded bg-white">
                {formData.cup_comparables.comparables_used.map((comp, i) => (
                  <div key={i} className="flex items-start justify-between px-3 py-2 gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{comp.title || '—'}</p>
                      <p className="text-xs text-gray-500">
                        {[comp.artist, comp.source, comp.date ? String(comp.date).slice(0, 10) : null].filter(Boolean).join(' · ')}
                      </p>
                      {comp.url && (
                        <a
                          href={comp.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] text-blue-600 hover:underline truncate block"
                        >
                          {comp.url.length > 60 ? comp.url.slice(0, 60) + '…' : comp.url}
                        </a>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-900 flex-shrink-0">
                      {comp.price ? formatCurrencyAmount(comp.price, valuationCurrency, i18n.language) : '—'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Description */}
      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700"
        >
          {t("artworkEdit.detail.description")}
        </label>
        <textarea
          id="description"
          name="description"
          value={formData?.description || ""}
          onChange={handleInputChange}
          rows={4}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          placeholder={t("artworkEdit.detail.descriptionPlaceholder")}
        />
      </div>

      {/* Tags / mots-clés */}
      <div className="border p-4 rounded-md space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">{t("artworkEdit.detail.tagsTitle")}</h3>
          <div className="flex items-center gap-2 text-xs">
            <span>{t("artworkEdit.detail.tagsMode")}</span>
            <select
              value={taggingMethod}
              onChange={(e) => setTaggingMethod(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1 text-xs"
            >
              <option value="manual">{t("artworkEdit.detail.tagsManual")}</option>
              <option value="local">{t("artworkEdit.detail.tagsLocal")}</option>
              <option value="api">{t("artworkEdit.detail.tagsAI")}</option>
            </select>
          </div>
        </div>

        {/* Saisie manuelle — TOUJOURS disponible, quel que soit le mode.
            Les mots-clés sont indispensables (pairing + analyse curatoriale) :
            le champ ne doit jamais disparaître. Le mode ne fait qu'ajouter les
            suggestions (locales / IA) par-dessus. */}
        <div className="flex gap-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag(tagInput);
              }
            }}
            placeholder={t("artworkEdit.detail.tagsPlaceholder")}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => addTag(tagInput)}
            className="px-3 py-2 text-sm bg-gray-900 text-white rounded-md"
          >
            {t("artworkEdit.detail.tagsAdd")}
          </button>
        </div>

        {suggestions.length > 0 && showTagSuggestions && (
          <ul className="border border-gray-300 mt-1 rounded-md bg-white shadow-sm max-h-40 overflow-y-auto">
            {suggestions.map((word, index) => (
              <li
                key={index}
                className="px-3 py-1 cursor-pointer hover:bg-gray-100 text-sm"
                onClick={() => addTag(word)}
              >
                {word}
              </li>
            ))}
          </ul>
        )}

        {/* Méthode locale */}
        {taggingMethod === "local" &&
          formData?.description &&
          showTagSuggestions && (
            <AdvancedTagsGenerator
              description={formData.description}
              currentTags={formData?.tags || []}
              setTags={updateTags}
            />
          )}

        {/* Méthode API — l'agent Artkiv (backend, notre clé serveur) génère les
            mots-clés dès que le mode IA est choisi, à partir de TOUT le contexte
            de l'œuvre (titre, artiste, médium, année + description). Plus de seuil
            de 50 caractères qui cachait le panneau. Réservé premium/advisor via
            getTagPermissions() ; endpoint forcé sur "openai" (= /api/analyse-tags). */}
        {taggingMethod === "api" && (
          <>
            <AiDisclosure
              type="suggested"
              className="mb-2"
              note={t("artworkEdit.detail.aiTagsNote", {
                defaultValue: "Mots-clés proposés par l'IA à partir du contexte de l'œuvre. Vérifiez-les avant de les enregistrer.",
              })}
            />
            <ApiTagsGenerator
              description={aiTagContext}
              currentTags={formData?.tags || []}
              setTags={updateTags}
              apiEndpoint="openai"
            />
          </>
        )}

        {/* Tags sélectionnés */}
        <div className="flex flex-wrap gap-2 mt-2">
          {(formData?.tags || []).map((tag, index) => (
            <span
              key={index}
              className="flex items-center gap-1 bg-gray-200 px-2 py-1 rounded-full text-xs"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="text-gray-500 hover:text-red-600 ml-1"
                title={t("artworkEdit.detail.tagsRemove")}
              >
                ✕
              </button>
            </span>
          ))}
        </div>

        {(formData?.tags || []).length > 0 && (
          <>
            <button
              type="button"
              onClick={clearTags}
              className="text-xs text-red-600 hover:underline mt-2"
            >
              {t("artworkEdit.detail.tagsClear")}
            </button>
            <div
              className={`text-xs mt-2 ${
                formData.tags && formData.tags.length >= 10
                  ? "text-red-600 font-semibold"
                  : "text-gray-500"
              }`}
            >
              {formData?.tags?.length || 0} {t("artworkEdit.detail.tagsCount")}
            </div>
          </>
        )}

        {/* ✨ Mots-clés IA (keywords_ai) */}
        {formData?.keywords_ai && formData.keywords_ai.length > 0 && (
          <div style={{
            marginTop: 12,
            borderTop: "1px solid #f0ede8",
            paddingTop: 12,
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}>
              <div style={{
                fontSize: 11,
                fontFamily: "system-ui",
                fontWeight: 600,
                color: "#c9a961",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}>
                ✦ {t("artworkEdit.detail.aiTags.title")}
              </div>
              <button
                type="button"
                onClick={() => {
                  const existing = formData?.tags || [];
                  const toAdd = formData.keywords_ai.filter(k => !existing.includes(k));
                  const merged = [...existing, ...toAdd].slice(0, 10);
                  updateTags(merged);
                }}
                style={{
                  fontSize: 11,
                  fontFamily: "system-ui",
                  color: "#c9a961",
                  background: "none",
                  border: "1px solid #c9a961",
                  borderRadius: 4,
                  padding: "2px 8px",
                  cursor: "pointer",
                }}
              >
                {t("artworkEdit.detail.aiTags.addAll")}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.keywords_ai.map((kw, i) => {
                const alreadyAdded = (formData?.tags || []).includes(kw);
                return (
                  <span
                    key={i}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      background: alreadyAdded ? "#f0ede8" : "#fdf9f0",
                      border: "1px solid #e5dfc8",
                      borderRadius: 20,
                      padding: "3px 10px",
                      fontSize: 12,
                      fontFamily: "system-ui",
                      color: alreadyAdded ? "#aaa" : "#1a1a1a",
                    }}
                  >
                    {kw}
                    {!alreadyAdded && (
                      <button
                        type="button"
                        onClick={() => addTag(kw)}
                        title={t("artworkEdit.detail.aiTags.addToTags")}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#c9a961",
                          fontSize: 14,
                          lineHeight: 1,
                          padding: 0,
                          marginLeft: 2,
                        }}
                      >
                        +
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>


      {/* Modal normalisation année */}
      {yearModalData && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setYearModalData(null)}
        >
          <div
            style={{
              background: "#fdfcfa", maxWidth: "440px", width: "90%",
              padding: "40px 36px", fontFamily: "Georgia, serif",
              border: "1px solid #e8e3db",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête */}
            <p style={{ fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#c9a961", marginBottom: "16px" }}>
              {t("artworkEdit.yearModal.label")}
            </p>
            <h3 style={{ fontSize: "22px", fontWeight: "normal", color: "#1a1a1a", margin: "0 0 12px" }}>
              {yearModalData.year}
            </h3>
            <p style={{ fontSize: "14px", color: "#555", lineHeight: "1.7", margin: "0 0 28px" }}>
              {t("artworkEdit.yearModal.body")} <strong>{t("artworkEdit.yearModal.jan1", { year: yearModalData.year })}</strong> {t("artworkEdit.yearModal.bodyEnd")}
            </p>
            <p style={{ fontSize: "12px", color: "#999", fontStyle: "italic", marginBottom: "32px" }}>
              {t("artworkEdit.yearModal.hint")}
            </p>
            {/* Actions */}
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setYearModalData(null)}
                style={{
                  background: "none", border: "1px solid #d0c9be", color: "#555",
                  padding: "9px 20px", fontFamily: "Georgia, serif", fontSize: "13px",
                  letterSpacing: "0.05em", cursor: "pointer",
                }}
              >
                {t("artworkEdit.yearModal.keepYear")}
              </button>
              <button
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    year: yearModalData.year,
                    _yearNormalizedToJan1: true,
                  }));
                  setYearModalData(null);
                }}
                style={{
                  background: "#1a1a1a", border: "none", color: "#f9f7f4",
                  padding: "9px 20px", fontFamily: "Georgia, serif", fontSize: "13px",
                  letterSpacing: "0.05em", cursor: "pointer",
                }}
              >
                {t("artworkEdit.yearModal.confirm", { date: `01/01/${yearModalData.year}` })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtworkDetailTab;